import { useEffect, useMemo } from 'react';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { M_REGULAR_COLLECTION_URLS } from 'constants/apiConstants';
import { M_CROPS_FETCH_PAGE_SIZE } from 'constants/appConstants';
import { MasterDbService } from 'services/MasterDbServices';

export const useFetchMCollection = <T>(
  collectionKey: keyof typeof M_REGULAR_COLLECTION_URLS | null,
  mOrgId: string | null,
  queryKey: unknown[]
) => {
  const query = useInfiniteQuery<
    T[],
    Error,
    InfiniteData<T[], number>,
    unknown[],
    number
  >({
    queryKey: [...queryKey],
    queryFn: ({ pageParam }): Promise<T[]> => {
      if (!collectionKey || !mOrgId) return Promise.resolve([]);
      // FetchMRegularCollectionData returns Promise<any>; assignable to
      // Promise<T[]> without a cast. The T[] contract is enforced by callers
      // that pair the collectionKey with a typed hook (e.g. useFetchMSeedSpecies).
      return MasterDbService.FetchMRegularCollectionData(
        collectionKey,
        mOrgId,
        M_CROPS_FETCH_PAGE_SIZE,
        pageParam
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === M_CROPS_FETCH_PAGE_SIZE
        ? allPages.length + 1
        : undefined,
    enabled: !!mOrgId && !!collectionKey,
    staleTime: 1000 * 60 * 5,
  });

  const {
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = query;

  // Stop on a failed page; otherwise this effect would re-request it in a loop
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  // Memoised so callers get a stable array between renders
  const data = useMemo(() => query.data?.pages.flat(), [query.data]);

  return {
    ...query,
    data,
  };
};
